

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Phone,
  CheckCircle,
  Tag,
  Calendar,
} from "lucide-react";
import styled from "styled-components";
import { Lead } from "../../types/api";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0; // Não é prop customizada, é CSS
  right: 0;
  bottom: 0;
  background: ${(props) => (props.theme.isLight ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.7)')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  width: 90%;
  max-width: 600px;
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  max-height: 70vh;
  overflow-y: auto;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing[5]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[3]};
`;

const DetailIcon = styled.div`
  color: ${props => props.theme.colors.primary};
  margin-top: 2px;
`;

const DetailText = styled.div``;

const DetailLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[1]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${(props) => {
    const t = props.theme;
    switch (props.$status?.toLowerCase()) {
      case "novo":
        return `background: rgba(0, 170, 255, 0.1); color: ${t.colors.info}; border: 1px solid rgba(0, 170, 255, 0.2);`;
      case "qualificado":
        return `background: rgba(0, 255, 136, 0.1); color: ${t.colors.success}; border: 1px solid rgba(0, 255, 136, 0.2);`;
      case "prospect":
        return `background: rgba(255, 170, 0, 0.1); color: ${t.colors.warning}; border: 1px solid rgba(255, 170, 0, 0.2);`;
      case "convertido":
        return `background: rgba(68, 255, 68, 0.1); color: ${t.colors.primary}; border: 1px solid rgba(68, 255, 68, 0.2);`;
      case "perdido":
        return `background: rgba(255, 68, 68, 0.1); color: ${t.colors.error}; border: 1px solid rgba(255, 68, 68, 0.2);`;
      default:
        return `background: ${t.colors.backgroundTertiary}; color: ${t.colors.textSecondary}; border: 1px solid ${t.colors.border};`;
    }
  }}
`;

interface LeadDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
  isOpen,
  onClose,
  lead,
}) => {
  if (!lead) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <ModalContent
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.2 }} // Framer Motion, não styled-components
          >
            <ModalHeader>
              <ModalTitle>
                <User />
                Detalhes do Lead
              </ModalTitle>
              <CloseButton onClick={onClose}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailsGrid>
                <DetailItem>
                  <DetailIcon>
                    <User size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Nome</DetailLabel>
                    <DetailValue>{lead.nome}</DetailValue>
                  </DetailText>
                </DetailItem>
                <DetailItem>
                  <DetailIcon>
                    <Phone size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Telefone</DetailLabel>
                    <DetailValue>{lead.telefone}</DetailValue>
                  </DetailText>
                </DetailItem>
                <DetailItem>
                  <DetailIcon>
                    <CheckCircle size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue>
                      <StatusBadge $status={lead.status}>{lead.status}</StatusBadge>
                    </DetailValue>
                  </DetailText>
                </DetailItem>
                <DetailItem>
                  <DetailIcon>
                    <Tag size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Interesse</DetailLabel>
                    <DetailValue>{lead.interesse || "Não informado"}</DetailValue>
                  </DetailText>
                </DetailItem>
                <DetailItem>
                  <DetailIcon>
                    <Calendar size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Data de Criação</DetailLabel>
                    <DetailValue>
                      {format(new Date(lead.dataCriacao), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </DetailValue>
                  </DetailText>
                </DetailItem>
                <DetailItem>
                  <DetailIcon>
                    <Calendar size={18} />
                  </DetailIcon>
                  <DetailText>
                    <DetailLabel>Último Contato</DetailLabel>
                    <DetailValue>
                      {format(new Date(lead.ultimoContato), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </DetailValue>
                  </DetailText>
                </DetailItem>
                {/* Email e Observações removidos */}
              </DetailsGrid>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default LeadDetailsModal;
